import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Icon } from "./Icon";
import { PdrReasoningBlock } from "./PdrReasoningBlock";
import { RelatedProjectsPanel } from "./RelatedProjectsPanel";
import { RoadmapDiscoveryPanel } from "./RoadmapDiscoveryPanel";
import { useToast } from "./Toast";
import { Button, Field, FieldLabel, Input, Select, Textarea } from "./ui";
import {
  ApiError,
  getMention,
  listProjectMerges,
  listProjectParticipants,
  listProjectRevisions,
  mergeProjects,
  revertProjectRevision,
  setProjectPairDecision,
  splitProject,
  undoProjectMerge,
} from "../lib/api";
import type { InitiativeMention, MergeEvent, ProjectRevision } from "../lib/types";
import {
  PDR_PROFILE_COMPLETE,
  fieldHasContent,
  fieldLabel,
  fieldQuote,
  fieldReasoning,
  fieldSourceUrl,
  fieldValue,
  normalizePdrProfile,
  pdrProfileDisplayLabel,
  tableFieldKeys,
  type ProjectData,
} from "../lib/pdrFields";
import { discoverySourceTagClass } from "../lib/discoverySources";
import { formatApiError } from "../lib/utils";

export type LineageRow = {
  fact: string;
  url?: string;
  title?: string;
  evidence_id?: string;
  discovery_mode?: string;
};

export type ProjectDetail = {
  project_id: string;
  project_slug: string;
  project_name: string;
  target_company: string;
  vendor_name: string;
  last_updated_at: string;
  cluster_id?: string;
  /**
   * Served by both GET /api/projects and the detail read. The identity panel
   * still recovers it from a mention if a record somehow omits it, so the
   * split write degrades rather than breaking.
   */
  target_company_id?: string;
  status?: string;
  /** project_aliases rows, straight from the detail read. */
  aliases?: string[];
  /** True when PDR was synthesized with audit reasoning enabled. */
  pdr_reasoning_enabled?: boolean;
  pdr_schema_profile?: string;
  discovery_sources?: string[];
  participants?: { vendor_name: string; role?: string; uncertainty?: boolean; cluster_id?: string }[];
  delivering_participants?: {
    vendor_name: string;
    role?: string;
    uncertainty?: boolean;
    cluster_id?: string;
  }[];
  project_data: ProjectData;
  lineage?: LineageRow[];
};

/* ========================================================================== *
 * Identity operations: shared helpers
 * ========================================================================== */

/**
 * A project the server deliberately did NOT re-home during a merge because the
 * move would collide with `uq_projects_target_contractor_slug`.
 *
 * The shape is read defensively rather than imported: the vendor merge route
 * returns `conflicts` today, the project merge route does not yet, and a
 * conflict list that arrives in a 409 body must be surfaced just the same. An
 * operator who never sees this believes a merge finished when it did not.
 */
type MergeConflict = {
  project_id?: string;
  project_slug?: string;
  kept_main_contractor?: string;
  blocking_project_id?: string;
  reason?: string;
};

function readConflicts(source: unknown): MergeConflict[] {
  if (!source || typeof source !== "object") return [];
  const raw = (source as Record<string, unknown>).conflicts;
  if (!Array.isArray(raw)) return [];
  return raw.map((item) =>
    item && typeof item === "object" ? (item as MergeConflict) : { reason: String(item) },
  );
}

/** Conflicts can arrive on the envelope, nested under `merge`, or inside a 409. */
function conflictsFromResponse(response: unknown): MergeConflict[] {
  const top = readConflicts(response);
  if (top.length) return top;
  if (response && typeof response === "object") {
    return readConflicts((response as Record<string, unknown>).merge);
  }
  return [];
}

function conflictsFromError(error: unknown): MergeConflict[] {
  if (!(error instanceof ApiError) || !error.body) return [];
  const top = readConflicts(error.body);
  if (top.length) return top;
  return readConflicts(error.body.detail);
}

function shortId(value: string): string {
  const text = String(value || "");
  return text.length > 12 ? `${text.slice(0, 8)}…` : text;
}

function truncate(text: string, max = 180): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

/** Revision payloads are raw JSON, so this never assumes the PDR field shape. */
function revisionValueText(data: Record<string, unknown> | null | undefined, key: string): string {
  const raw = data ? data[key] : undefined;
  if (raw === null || raw === undefined) return "";
  if (typeof raw === "string") return raw;
  if (typeof raw === "number" || typeof raw === "boolean") return String(raw);
  if (typeof raw === "object" && "value" in (raw as Record<string, unknown>)) {
    const inner = (raw as Record<string, unknown>).value;
    return inner === null || inner === undefined ? "" : String(inner);
  }
  try {
    return JSON.stringify(raw);
  } catch {
    return String(raw);
  }
}

function changedKeys(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined,
): string[] {
  const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
  return [...keys]
    .filter((key) => revisionValueText(before, key) !== revisionValueText(after, key))
    .sort();
}

function causeSummary(cause: Record<string, unknown> | null | undefined): string {
  if (!cause || typeof cause !== "object") return "unrecorded";
  const kind = String(cause.kind || "").trim() || "change";
  const extras = Object.entries(cause)
    .filter(([key]) => key !== "kind")
    .map(([key, value]) => {
      const text = value && typeof value === "object" ? JSON.stringify(value) : String(value);
      return `${key}=${truncate(text, 60)}`;
    });
  return extras.length ? `${kind} · ${extras.join(" · ")}` : kind;
}

function newCreationKey(projectId: string): string {
  const suffix =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `split:${projectId}:${suffix}`;
}

const UNSETTLED_MENTION_STATUSES = new Set(["pending", "insufficient"]);
/** Mentions inspected for duplicate candidates on load; each costs one GET. */
const CANDIDATE_SCAN_LIMIT = 6;

/* ========================================================================== *
 * Small presentational pieces
 * ========================================================================== */

function Tag({
  children,
  tone = "neutral",
  title,
}: {
  children: ReactNode;
  tone?: "neutral" | "primary" | "secondary" | "tertiary" | "error";
  title?: string;
}) {
  const tones = {
    neutral: "bg-surface-container-high text-on-surface-variant border-outline-variant",
    primary: "bg-primary/10 text-primary border-primary/20",
    secondary: "bg-secondary/10 text-secondary border-secondary/20",
    tertiary: "bg-tertiary/10 text-tertiary border-tertiary/20",
    error: "bg-error/10 text-error border-error/40",
  } as const;
  return (
    <span
      className={`text-[9px] font-bold px-2 py-0.5 rounded border uppercase ${tones[tone]}`}
      title={title}
    >
      {children}
    </span>
  );
}

function Panel({
  title,
  icon,
  meta,
  actions,
  children,
  collapsible = false,
  defaultOpen = true,
}: {
  title: string;
  icon: string;
  meta?: string;
  actions?: ReactNode;
  children: ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = collapsible ? open : true;
  return (
    <section className="panel-border p-4 bg-surface-container-low">
      <div className="flex items-center justify-between gap-3">
        {collapsible ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={isOpen}
            className="flex items-center gap-2 text-left min-w-0"
          >
            <Icon
              name={isOpen ? "expand_less" : "expand_more"}
              className="text-base text-on-surface-variant"
            />
            <span className="text-[10px] uppercase tracking-widest text-on-surface-variant font-data-label">
              {title}
            </span>
            {meta ? (
              <span className="text-[10px] font-data-mono text-outline truncate">{meta}</span>
            ) : null}
          </button>
        ) : (
          <h3 className="text-[10px] uppercase tracking-widest text-on-surface-variant font-data-label flex items-center gap-2 min-w-0">
            <Icon name={icon} className="text-base" />
            {title}
            {meta ? (
              <span className="text-[10px] font-data-mono text-outline normal-case tracking-normal truncate">
                {meta}
              </span>
            ) : null}
          </h3>
        )}
        {actions ? <div className="flex items-center gap-2 shrink-0">{actions}</div> : null}
      </div>
      {isOpen ? <div className="mt-3">{children}</div> : null}
    </section>
  );
}

/** Every operator surface here must show its own failure rather than nothing. */
function ErrorNote({ message, onRetry }: { message: string; onRetry?: () => void }) {
  if (!message) return null;
  return (
    <div className="border border-error/40 bg-error/10 rounded px-3 py-2 flex items-start gap-2">
      <Icon name="error" className="text-base text-error shrink-0" />
      <p className="text-body-sm text-error leading-relaxed min-w-0 break-words">{message}</p>
      {onRetry ? (
        <Button variant="link-muted" onClick={onRetry} className="shrink-0">
          Retry
        </Button>
      ) : null}
    </div>
  );
}

function ConfirmDialog({
  open,
  title,
  intent,
  confirmLabel,
  confirmDisabled,
  busy,
  onCancel,
  onConfirm,
  children,
}: {
  open: boolean;
  title: string;
  intent: string;
  confirmLabel: string;
  confirmDisabled?: boolean;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  children?: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-surface-dim/85 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="w-full max-w-xl panel-border bg-surface-container flex flex-col max-h-[85vh]">
        <header className="px-5 py-4 border-b border-outline-variant shrink-0">
          <h4 className="font-headline-md text-headline-md text-on-surface">{title}</h4>
          <p className="text-body-sm text-on-surface-variant mt-1 leading-relaxed">{intent}</p>
        </header>
        <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-4 space-y-4">{children}</div>
        <footer className="px-5 py-3 border-t border-outline-variant flex justify-end gap-2 shrink-0">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={busy || confirmDisabled}>
            {busy ? "Working…" : confirmLabel}
          </Button>
        </footer>
      </div>
    </div>
  );
}

/* ========================================================================== *
 * Merge conflicts
 * ========================================================================== */

function MergeConflictBanner({
  conflicts,
  onDismiss,
}: {
  conflicts: MergeConflict[];
  onDismiss: () => void;
}) {
  if (!conflicts.length) return null;
  return (
    <div className="sticky top-0 z-20 border-2 border-error bg-error/15 rounded p-4 shadow-lg">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-body-base font-bold text-error flex items-center gap-2">
          <Icon name="warning" className="text-lg" />
          The merge did not re-home {conflicts.length} project
          {conflicts.length === 1 ? "" : "s"}
        </h3>
        <Button variant="link-muted" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
      <p className="text-body-sm text-on-surface mt-2 leading-relaxed">
        These projects were left where they were because moving them would collide with the slug
        uniqueness index (<span className="font-data-mono">uq_projects_target_contractor_slug</span>
        ). The merge is <span className="font-bold">incomplete</span> until each one is resolved by
        hand — rename or merge the blocking project first, then retry.
      </p>
      <ul className="mt-3 space-y-2">
        {conflicts.map((conflict, index) => (
          <li
            key={`${conflict.project_id || "conflict"}-${index}`}
            className="border-t border-error/30 pt-2 first:border-0 first:pt-0"
          >
            <p className="text-body-sm font-data-mono text-on-surface break-all">
              {conflict.project_slug || conflict.project_id || conflict.reason || "unnamed conflict"}
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-[10px] font-data-mono text-on-surface-variant">
              {conflict.project_id ? (
                <span className="select-all break-all">project {conflict.project_id}</span>
              ) : null}
              {conflict.blocking_project_id ? (
                <span className="select-all break-all">
                  blocked by {conflict.blocking_project_id}
                </span>
              ) : null}
              {conflict.kept_main_contractor ? (
                <span>kept contractor {conflict.kept_main_contractor}</span>
              ) : null}
              {conflict.reason ? <span>{conflict.reason}</span> : null}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ========================================================================== *
 * Participants (plan 5.4): evidenced delivery vs. merely mentioned
 * ========================================================================== */

type ParticipantLike = {
  vendor_name: string;
  role?: string;
  uncertainty?: boolean;
  cluster_id?: string;
};

function participantKey(participant: ParticipantLike): string {
  return `${participant.vendor_name}::${participant.cluster_id || ""}`;
}

function ParticipantsPanel({
  targetCompany,
  participants,
  delivering,
  loading,
  error,
  onRetry,
}: {
  targetCompany: string;
  participants: ParticipantLike[];
  delivering: ParticipantLike[];
  loading: boolean;
  error: string;
  onRetry: () => void;
}) {
  const deliveringNames = useMemo(
    () => new Set(delivering.map((p) => p.vendor_name)),
    [delivering],
  );

  return (
    <Panel
      title="Organizations"
      icon="groups"
      meta={loading ? "loading…" : `${participants.length} participating`}
    >
      <div className="space-y-4">
        {error ? <ErrorNote message={error} onRetry={onRetry} /> : null}

        <div>
          <FieldLabel className="text-primary">Main contractor · evidenced delivery role</FieldLabel>
          {!delivering.length ? (
            <p className="text-body-sm text-on-surface-variant mt-1 leading-relaxed">
              No participant has an evidenced delivering role. Per the PDR rule the Main Contractor
              field stays N/A — this is a recorded absence of evidence, not a missing lookup.
            </p>
          ) : (
            <ul className="mt-2 space-y-1.5">
              {delivering.map((participant) => (
                <li
                  key={participantKey(participant)}
                  className="flex flex-wrap items-center gap-2 border border-primary/30 bg-primary/5 rounded px-3 py-2"
                >
                  <span className="text-body-sm font-semibold text-on-surface">
                    {participant.vendor_name}
                  </span>
                  <Tag tone="primary" title="Role evidenced by a mention">
                    {participant.role || "delivering"}
                  </Tag>
                  {participant.uncertainty ? (
                    <Tag tone="tertiary" title="Recorded with uncertainty">
                      provisional
                    </Tag>
                  ) : null}
                  {participant.cluster_id ? (
                    <ClusterLink
                      clusterId={participant.cluster_id}
                      targetCompany={targetCompany}
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <FieldLabel>Involved organizations · every participation</FieldLabel>
          {!participants.length ? (
            <p className="text-body-sm text-on-surface-variant mt-1 leading-relaxed">
              No participation rows. A project written by the legacy path has none, which is
              expected while ORION1_MENTIONS_V3 is off.
            </p>
          ) : (
            <ul className="mt-2 space-y-1.5">
              {participants.map((participant) => (
                <li
                  key={participantKey(participant)}
                  className="flex flex-wrap items-center gap-2 border border-outline-variant rounded px-3 py-2 bg-surface-container-lowest"
                >
                  <span className="text-body-sm text-on-surface">{participant.vendor_name}</span>
                  <Tag
                    tone={deliveringNames.has(participant.vendor_name) ? "primary" : "neutral"}
                    title={
                      deliveringNames.has(participant.vendor_name)
                        ? "Evidenced delivering role"
                        : "Named by evidence without an evidenced delivery role"
                    }
                  >
                    {participant.role || "mentioned"}
                  </Tag>
                  {participant.uncertainty ? (
                    <Tag tone="tertiary" title="Recorded with uncertainty">
                      provisional
                    </Tag>
                  ) : null}
                  {participant.cluster_id ? (
                    <ClusterLink
                      clusterId={participant.cluster_id}
                      targetCompany={targetCompany}
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Panel>
  );
}

function ClusterLink({
  clusterId,
  targetCompany,
  label,
}: {
  clusterId: string;
  targetCompany: string;
  label?: string;
}) {
  const query = new URLSearchParams();
  if (targetCompany) query.set("target", targetCompany);
  query.set("bucket", clusterId);
  return (
    <Link
      to={`/vendor-clusters?${query.toString()}`}
      className="text-[10px] font-data-mono text-primary hover:underline truncate"
      title={`Open vendor clusters for cluster ${clusterId}`}
    >
      <Icon name="hub" className="text-xs align-middle mr-1" />
      {label || shortId(clusterId)}
    </Link>
  );
}

/* ========================================================================== *
 * Aliases and participating clusters
 * ========================================================================== */

function IdentityPanel({
  project,
  aliases,
  aliasSource,
  participants,
  evidenceNames,
  loading,
}: {
  project: ProjectDetail;
  aliases: string[];
  aliasSource: string;
  participants: ParticipantLike[];
  evidenceNames: string[];
  loading: boolean;
}) {
  const clusters = useMemo(() => {
    const seen = new Map<string, { id: string; owner: boolean; vendors: string[] }>();
    if (project.cluster_id) {
      seen.set(project.cluster_id, { id: project.cluster_id, owner: true, vendors: [] });
    }
    for (const participant of participants) {
      const id = participant.cluster_id || "";
      if (!id) continue;
      const entry = seen.get(id) || { id, owner: false, vendors: [] };
      if (participant.vendor_name && !entry.vendors.includes(participant.vendor_name)) {
        entry.vendors.push(participant.vendor_name);
      }
      seen.set(id, entry);
    }
    return [...seen.values()];
  }, [project.cluster_id, participants]);

  return (
    <Panel
      title="Aliases and participating clusters"
      icon="fingerprint"
      meta={loading ? "loading…" : `${aliases.length} alias${aliases.length === 1 ? "" : "es"}`}
    >
      <div className="space-y-4">
        <div>
          <FieldLabel>Recorded aliases</FieldLabel>
          {!aliases.length ? (
            <p className="text-body-sm text-on-surface-variant mt-1 leading-relaxed">
              {loading
                ? "Reading retrieval candidates…"
                : "No aliases recorded for this project id."}
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {aliases.map((alias) => (
                <span
                  key={alias}
                  className="text-body-sm font-data-mono px-2 py-1 rounded border border-outline-variant bg-surface-container-lowest text-on-surface"
                >
                  {alias}
                </span>
              ))}
            </div>
          )}
          {aliasSource ? (
            <p className="text-[10px] text-outline font-data-mono mt-2">{aliasSource}</p>
          ) : null}
        </div>

        {evidenceNames.length ? (
          <div>
            <FieldLabel>Names used by the evidence</FieldLabel>
            <p className="text-[10px] text-outline mt-1">
              Program names and aliases carried by this project&apos;s mentions. These are observed
              strings, not recorded aliases.
            </p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {evidenceNames.map((name) => (
                <span
                  key={name}
                  className="text-body-sm font-data-mono px-2 py-1 rounded border border-dashed border-outline-variant text-on-surface-variant"
                >
                  {name}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        <div>
          <FieldLabel>Participating clusters</FieldLabel>
          {!clusters.length ? (
            <p className="text-body-sm text-on-surface-variant mt-1 leading-relaxed">
              No cluster participation recorded for this project.
            </p>
          ) : (
            <ul className="mt-2 space-y-1.5">
              {clusters.map((cluster) => (
                <li
                  key={cluster.id}
                  className="flex flex-wrap items-center gap-2 border border-outline-variant rounded px-3 py-2 bg-surface-container-lowest"
                >
                  <ClusterLink
                    clusterId={cluster.id}
                    targetCompany={project.target_company}
                    label={cluster.id}
                  />
                  {cluster.owner ? (
                    <Tag tone="secondary" title="Processing owner: schedules this project's work">
                      processing owner
                    </Tag>
                  ) : null}
                  {cluster.vendors.map((vendor) => (
                    <Tag key={vendor}>{vendor}</Tag>
                  ))}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Panel>
  );
}

/* ========================================================================== *
 * Revision history with restore
 * ========================================================================== */

function RevisionsPanel({
  projectId,
  targetCompanyId,
  clusterId,
  refreshKey,
  onChanged,
}: {
  projectId: string;
  targetCompanyId: string;
  clusterId: string;
  refreshKey: number;
  onChanged: () => void;
}) {
  const { toast } = useToast();
  const [revisions, setRevisions] = useState<ProjectRevision[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pending, setPending] = useState<ProjectRevision | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const data = await listProjectRevisions(projectId);
      const rows = [...(data.revisions || [])].sort((a, b) => b.revision_no - a.revision_no);
      setRevisions(rows);
    } catch (e) {
      setRevisions([]);
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const latestRevision = revisions.length ? revisions[0].revision_no : 0;
  // The newest recorded revision's after_data is the stored state, so a diff
  // preview needs no extra read.  (A legacy `phase3_store.upsert_project`
  // write leaves no revision row, so this is the newest *recorded* state.)
  const currentData = revisions.length ? revisions[0].after_data : undefined;

  const confirmRestore = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const { revert } = await revertProjectRevision(projectId, {
        revisionNo: pending.revision_no,
        // Optional: the route reads the project's own when this is "".  Passed
        // when known so the cross-target scope check still runs.
        targetCompanyId,
        clusterId,
        expectedRevision: latestRevision || null,
      });
      toast(
        `Restored the state of revision ${revert.reverted_to}; the restore is itself revision ${revert.revision_no}.`,
      );
      setPending(null);
      await load();
      onChanged();
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setBusy(false);
    }
  };

  // What the restore will change, measured against the stored state — not the
  // change this revision made.  The route restores `after_data`.
  const pendingKeys = pending ? changedKeys(currentData, pending.after_data) : [];

  return (
    <Panel
      title="Revision history"
      icon="history"
      meta={loading ? "loading…" : `${revisions.length} revision${revisions.length === 1 ? "" : "s"}`}
      actions={
        <Button variant="link-muted" onClick={() => void load()} disabled={loading}>
          Reload
        </Button>
      }
      collapsible
      defaultOpen={false}
    >
      <div className="space-y-3">
        {error ? <ErrorNote message={error} onRetry={() => void load()} /> : null}
        {!revisions.length && !loading && !error ? (
          <p className="text-body-sm text-on-surface-variant leading-relaxed">
            No revisions recorded. Legacy-path projects written by
            <span className="font-data-mono"> phase3_store.upsert_project </span>
            do not write revision rows.
          </p>
        ) : null}

        {revisions.map((revision) => {
          const keys = changedKeys(revision.before_data, revision.after_data);
          const isCurrent = revision.revision_no === latestRevision;
          return (
            <article
              key={revision.revision_no}
              className="border border-outline-variant rounded bg-surface-container-lowest"
            >
              <header className="px-3 py-2 flex flex-wrap items-center gap-2 border-b border-outline-variant/60">
                <Tag tone="primary">rev {revision.revision_no}</Tag>
                <span className="text-[10px] font-data-mono text-on-surface-variant">
                  {revision.created_at
                    ? new Date(revision.created_at).toLocaleString()
                    : "no timestamp"}
                </span>
                <span className="text-[10px] font-data-mono text-on-surface-variant">
                  actor {revision.actor || "unknown"}
                </span>
                {revision.model ? (
                  <Tag tone="tertiary" title="Model responsible for this change">
                    {revision.model}
                  </Tag>
                ) : null}
                <span className="text-[10px] font-data-mono text-outline truncate">
                  cause: {causeSummary(revision.cause)}
                </span>
                <div className="ml-auto">
                  {isCurrent ? (
                    <span className="text-[10px] text-outline font-data-mono">
                      the stored state — nothing to restore
                    </span>
                  ) : (
                    <Button
                      variant="link-muted"
                      onClick={() => setPending(revision)}
                      title="Write the state this revision recorded back as a new revision"
                    >
                      Restore this state
                    </Button>
                  )}
                </div>
              </header>
              <div className="px-3 py-2">
                {!keys.length ? (
                  <p className="text-body-sm text-on-surface-variant">
                    No PDR field values changed (identity or participation change only).
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {keys.map((key) => (
                      <li key={key} className="text-body-sm">
                        <p className="text-[10px] uppercase tracking-widest text-on-surface-variant font-data-label">
                          {fieldLabel(key)}
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
                          <div className="border border-outline-variant/60 rounded px-2 py-1">
                            <p className="text-[9px] uppercase text-outline">before</p>
                            <p className="text-on-surface-variant break-words whitespace-pre-wrap">
                              {truncate(revisionValueText(revision.before_data, key)) || "—"}
                            </p>
                          </div>
                          <div className="border border-primary/30 rounded px-2 py-1">
                            <p className="text-[9px] uppercase text-primary">after</p>
                            <p className="text-on-surface break-words whitespace-pre-wrap">
                              {truncate(revisionValueText(revision.after_data, key)) || "—"}
                            </p>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </article>
          );
        })}
      </div>

      <ConfirmDialog
        open={Boolean(pending)}
        title={`Restore the state of revision ${pending?.revision_no ?? ""}`}
        intent="This writes the state that revision recorded back onto the project as a new revision. History is not erased; the restore is itself auditable."
        confirmLabel="Restore"
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={() => void confirmRestore()}
      >
        <p className="text-body-sm text-on-surface-variant leading-relaxed">
          The write is guarded with{" "}
          <span className="font-data-mono">expected_revision = {latestRevision}</span>, so it fails
          with a conflict rather than overwriting anything saved since this page loaded.
        </p>
        {pendingKeys.length ? (
          <div>
            <FieldLabel>Fields this restore will change</FieldLabel>
            <ul className="mt-2 space-y-1 text-body-sm font-data-mono text-on-surface-variant">
              {pendingKeys.map((key) => (
                <li key={key} className="break-words">
                  {fieldLabel(key)}: {truncate(revisionValueText(currentData, key), 60) || "—"}
                  {" → "}
                  {truncate(revisionValueText(pending?.after_data, key), 60) || "—"}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-body-sm text-on-surface-variant">
            No PDR field values differ from the stored state; the restore will still write an
            auditable revision.
          </p>
        )}
      </ConfirmDialog>
    </Panel>
  );
}

/* ========================================================================== *
 * Merge, split, "not the same as", duplicate candidates
 * ========================================================================== */

type DuplicateCandidate = {
  project_id: string;
  name: string;
  aliases: string[];
  cross_vendor: boolean;
  mention_ids: string[];
  unsettled: boolean;
};

type DialogKind = "merge" | "split" | "pair" | null;

function OperationsPanel({
  project,
  targetCompanyId,
  mentions,
  candidates,
  candidatesLoading,
  candidatesError,
  onRescan,
  onConflicts,
  onChanged,
  onOpenProject,
}: {
  project: ProjectDetail;
  targetCompanyId: string;
  mentions: InitiativeMention[];
  candidates: DuplicateCandidate[];
  candidatesLoading: boolean;
  candidatesError: string;
  onRescan: () => void;
  onConflicts: (conflicts: MergeConflict[]) => void;
  onChanged: () => void;
  onOpenProject?: (projectId: string) => void;
}) {
  const { toast } = useToast();
  const projectId = project.project_id;

  const [dialog, setDialog] = useState<DialogKind>(null);
  const [busy, setBusy] = useState(false);

  const [otherId, setOtherId] = useState("");
  const [absorbOther, setAbsorbOther] = useState(true);

  const [splitName, setSplitName] = useState("");
  const [splitMentions, setSplitMentions] = useState<string[]>([]);
  const [creationKey, setCreationKey] = useState("");

  const [verdict, setVerdict] = useState("different");
  const [reasoning, setReasoning] = useState("");

  const [merges, setMerges] = useState<MergeEvent[]>([]);
  const [mergesError, setMergesError] = useState("");
  const [mergesLoading, setMergesLoading] = useState(false);

  const loadMerges = useCallback(async () => {
    if (!projectId) return;
    setMergesLoading(true);
    setMergesError("");
    try {
      const data = await listProjectMerges({
        project_id: projectId,
        include_reversed: true,
        limit: 25,
      });
      setMerges(data.merges || []);
    } catch (e) {
      setMerges([]);
      setMergesError(formatApiError(e));
    } finally {
      setMergesLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void loadMerges();
  }, [loadMerges]);

  const closeDialog = () => {
    setDialog(null);
    setBusy(false);
  };

  const openMerge = (candidateId = "") => {
    setOtherId(candidateId);
    setAbsorbOther(true);
    setDialog("merge");
  };

  const openPair = (candidateId = "") => {
    setOtherId(candidateId);
    setVerdict("different");
    setReasoning("");
    setDialog("pair");
  };

  const openSplit = () => {
    setSplitName("");
    setSplitMentions([]);
    setCreationKey(newCreationKey(projectId));
    setDialog("split");
  };

  const runMerge = async () => {
    const other = otherId.trim();
    if (!other) return;
    const survivor = absorbOther ? projectId : other;
    const absorbed = absorbOther ? other : projectId;
    setBusy(true);
    try {
      const result = await mergeProjects({
        survivor_project_id: survivor,
        absorbed_project_id: absorbed,
      });
      const conflicts = conflictsFromResponse(result);
      onConflicts(conflicts);
      const moved = result.merge?.moved_mentions ?? 0;
      toast(
        conflicts.length
          ? `Merged with ${conflicts.length} unresolved slug conflict${conflicts.length === 1 ? "" : "s"} — see the banner.`
          : `Merged: ${moved} mention${moved === 1 ? "" : "s"} moved into ${shortId(survivor)}.`,
        conflicts.length > 0,
      );
      closeDialog();
      await loadMerges();
      onChanged();
      if (!absorbOther && onOpenProject) onOpenProject(survivor);
    } catch (e) {
      const conflicts = conflictsFromError(e);
      if (conflicts.length) onConflicts(conflicts);
      toast(formatApiError(e), true);
      setBusy(false);
    }
  };

  const runSplit = async () => {
    if (!targetCompanyId || !splitMentions.length || !splitName.trim()) return;
    setBusy(true);
    try {
      const result = await splitProject(projectId, {
        target_company_id: targetCompanyId,
        mention_ids: splitMentions,
        creation_key: creationKey,
        project_data: { project_name: splitName.trim() },
        cluster_id: project.cluster_id || "",
      });
      toast(
        `Split: ${result.split.moved_mentions} mention(s) moved into ${shortId(result.split.new_project_id)}.`,
      );
      const newId = result.split.new_project_id;
      closeDialog();
      onChanged();
      if (onOpenProject && newId) onOpenProject(newId);
    } catch (e) {
      toast(formatApiError(e), true);
      setBusy(false);
    }
  };

  const runPairDecision = async () => {
    const other = otherId.trim();
    if (!other) return;
    setBusy(true);
    try {
      await setProjectPairDecision({
        project_a_id: projectId,
        project_b_id: other,
        verdict,
        reasoning: reasoning.trim(),
      });
      toast(`Recorded "${verdict}" between this project and ${shortId(other)}.`);
      closeDialog();
      onChanged();
    } catch (e) {
      toast(formatApiError(e), true);
      setBusy(false);
    }
  };

  const runUndo = async (mergeEventId: string) => {
    setBusy(true);
    try {
      const result = await undoProjectMerge(mergeEventId);
      toast(`Merge undone; ${shortId(result.undo.restored_project_id)} is active again.`);
      onConflicts([]);
      await loadMerges();
      onChanged();
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setBusy(false);
    }
  };

  const toggleSplitMention = (mentionId: string) => {
    setSplitMentions((current) =>
      current.includes(mentionId)
        ? current.filter((id) => id !== mentionId)
        : [...current, mentionId],
    );
  };

  const splitBlocked = !targetCompanyId
    ? "This project record carries no target_company_id, and the split route requires one."
    : !mentions.length
      ? "This project has no mentions to move."
      : "";

  return (
    <Panel
      title="Identity operations"
      icon="call_split"
      meta={`${candidates.length} duplicate candidate${candidates.length === 1 ? "" : "s"}`}
      actions={
        <>
          <Button variant="secondary" size="sm" onClick={() => openMerge()}>
            <Icon name="merge" className="text-base" />
            Merge
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={openSplit}
            disabled={Boolean(splitBlocked)}
            title={splitBlocked || "Move selected mentions into a new project"}
          >
            <Icon name="call_split" className="text-base" />
            Split
          </Button>
          <Button variant="secondary" size="sm" onClick={() => openPair()}>
            <Icon name="do_not_disturb_on" className="text-base" />
            Not the same as
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-[10px] text-outline leading-relaxed">
          Merge, split and pair decisions change stored identity. Each is confirmed before it runs;
          a merge can be undone from the history below.
        </p>

        <div>
          <div className="flex items-center justify-between gap-2">
            <FieldLabel>
              Duplicate candidates · retrieval over this project&apos;s mentions
            </FieldLabel>
            <Button variant="link-muted" onClick={onRescan} disabled={candidatesLoading}>
              {candidatesLoading ? "Scanning…" : "Rescan"}
            </Button>
          </div>
          {candidatesError ? (
            <div className="mt-2">
              <ErrorNote message={candidatesError} onRetry={onRescan} />
            </div>
          ) : null}
          {!candidates.length && !candidatesLoading && !candidatesError ? (
            <p className="text-body-sm text-on-surface-variant mt-1 leading-relaxed">
              Retrieval offered no other project for this project&apos;s mentions. With
              ORION1_MENTIONS_V3 off there are no mentions to scan, so this stays empty.
            </p>
          ) : null}
          <ul className="mt-2 space-y-2">
            {candidates.map((candidate) => (
              <li
                key={candidate.project_id}
                className={`rounded px-3 py-2 border ${
                  candidate.cross_vendor
                    ? "border-tertiary/40 bg-tertiary/5"
                    : "border-outline-variant bg-surface-container-lowest"
                }`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-body-sm font-semibold text-on-surface">
                    {candidate.name || "Unnamed project"}
                  </span>
                  {candidate.cross_vendor ? (
                    <Tag
                      tone="tertiary"
                      title="Reached only through a strong cross-vendor identifier (plan 5.5 step 2)"
                    >
                      cross-vendor identifier
                    </Tag>
                  ) : null}
                  {candidate.unsettled ? (
                    <Tag tone="secondary" title="Offered for a mention that has no settled decision">
                      queued by escalation
                    </Tag>
                  ) : null}
                  <span className="text-[10px] font-data-mono text-on-surface-variant select-all break-all">
                    {candidate.project_id}
                  </span>
                  <div className="ml-auto flex items-center gap-2">
                    {onOpenProject ? (
                      <Button
                        variant="link-muted"
                        onClick={() => onOpenProject(candidate.project_id)}
                      >
                        Open
                      </Button>
                    ) : null}
                    <Button variant="link-muted" onClick={() => openMerge(candidate.project_id)}>
                      Merge…
                    </Button>
                    <Button variant="link-muted" onClick={() => openPair(candidate.project_id)}>
                      Not the same…
                    </Button>
                  </div>
                </div>
                {candidate.aliases.length ? (
                  <p className="text-[10px] font-data-mono text-on-surface-variant mt-1 break-words">
                    aliases: {candidate.aliases.join(" · ")}
                  </p>
                ) : null}
                <p className="text-[10px] font-data-mono text-outline mt-1">
                  offered for {candidate.mention_ids.length} mention
                  {candidate.mention_ids.length === 1 ? "" : "s"}
                </p>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="flex items-center justify-between gap-2">
            <FieldLabel>Merge history</FieldLabel>
            <Button variant="link-muted" onClick={() => void loadMerges()} disabled={mergesLoading}>
              Reload
            </Button>
          </div>
          {mergesError ? (
            <div className="mt-2">
              <ErrorNote message={mergesError} onRetry={() => void loadMerges()} />
            </div>
          ) : null}
          {!merges.length && !mergesLoading && !mergesError ? (
            <p className="text-body-sm text-on-surface-variant mt-1">
              No merge touched this project.
            </p>
          ) : null}
          <ul className="mt-2 space-y-2">
            {merges.map((event) => (
              <li
                key={event.merge_event_id}
                className="border border-outline-variant rounded px-3 py-2 bg-surface-container-lowest"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-body-sm text-on-surface">
                    {event.absorbed_project_name || shortId(event.absorbed_project_id)} →{" "}
                    {event.survivor_project_name || shortId(event.survivor_project_id)}
                  </span>
                  {event.reversed_at ? (
                    <Tag tone="neutral">reversed {new Date(event.reversed_at).toLocaleString()}</Tag>
                  ) : event.reversible ? (
                    <Tag tone="secondary">reversible</Tag>
                  ) : (
                    <Tag tone="error" title="Later accepted revisions block a clean undo">
                      not reversible
                    </Tag>
                  )}
                  <span className="text-[10px] font-data-mono text-outline">
                    {event.created_at ? new Date(event.created_at).toLocaleString() : ""}
                  </span>
                  {!event.reversed_at && event.reversible ? (
                    <Button
                      variant="link-muted"
                      className="ml-auto"
                      disabled={busy}
                      onClick={() => void runUndo(event.merge_event_id)}
                    >
                      Undo merge
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <ConfirmDialog
        open={dialog === "merge"}
        title="Merge two projects"
        intent="A merge moves mentions, evidence lineage and participation onto one surviving id and redirects the other. It is recorded as a merge event and can be undone."
        confirmLabel={absorbOther ? "Merge into this project" : "Merge this project away"}
        confirmDisabled={!otherId.trim() || otherId.trim() === projectId}
        busy={busy}
        onCancel={closeDialog}
        onConfirm={() => void runMerge()}
      >
        <Field label="Other project id">
          <Input
            value={otherId}
            onChange={(e) => setOtherId(e.target.value)}
            placeholder="project UUID"
          />
        </Field>
        {candidates.length ? (
          <Field label="Or pick a duplicate candidate">
            <Select value={otherId} onChange={(e) => setOtherId(e.target.value)}>
              <option value="">—</option>
              {candidates.map((candidate) => (
                <option key={candidate.project_id} value={candidate.project_id}>
                  {candidate.name || candidate.project_id}
                  {candidate.cross_vendor ? " (cross-vendor)" : ""}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field label="Direction">
          <Select
            value={absorbOther ? "absorb" : "absorbed"}
            onChange={(e) => setAbsorbOther(e.target.value === "absorb")}
          >
            <option value="absorb">Keep this project — absorb the other</option>
            <option value="absorbed">Keep the other project — absorb this one</option>
          </Select>
        </Field>
        <p className="text-body-sm text-error leading-relaxed">
          If the server refuses to re-home a project because of the slug uniqueness index, the
          conflicts are listed at the top of this view and the merge is incomplete until you resolve
          them.
        </p>
        {otherId.trim() === projectId ? (
          <p className="text-body-sm text-error">A project cannot be merged into itself.</p>
        ) : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === "split"}
        title="Split this project"
        intent="Selected mentions and the evidence they cite move to a new project. Nothing is copied: both records are re-derived from where their evidence now sits."
        confirmLabel="Split"
        confirmDisabled={!splitName.trim() || !splitMentions.length}
        busy={busy}
        onCancel={closeDialog}
        onConfirm={() => void runSplit()}
      >
        <Field label="New project name">
          <Input
            value={splitName}
            onChange={(e) => setSplitName(e.target.value)}
            placeholder="Name for the initiative being separated"
          />
        </Field>
        <div>
          <FieldLabel>
            Mentions to move ({splitMentions.length} of {mentions.length})
          </FieldLabel>
          <ul className="mt-2 space-y-1.5 max-h-64 overflow-y-auto custom-scrollbar">
            {mentions.map((mention) => (
              <li key={mention.mention_id}>
                <label className="flex items-start gap-2 border border-outline-variant rounded px-2 py-1.5 cursor-pointer hover:border-primary/40">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={splitMentions.includes(mention.mention_id)}
                    onChange={() => toggleSplitMention(mention.mention_id)}
                  />
                  <span className="min-w-0">
                    <span className="block text-body-sm text-on-surface break-words">
                      {truncate(mention.passage || mention.title || mention.mention_id, 160)}
                    </span>
                    <span className="block text-[10px] font-data-mono text-outline truncate">
                      {mention.resolution_status} · {mention.title || mention.url || "no source"}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
        <p className="text-[10px] font-data-mono text-outline break-all">
          creation key: {creationKey}
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === "pair"}
        title="Record a pair decision"
        intent='A persisted "not the same as" is respected by retrieval, batch classification and escalation, so the pair stops coming back as a duplicate candidate.'
        confirmLabel="Record decision"
        confirmDisabled={!otherId.trim() || otherId.trim() === projectId}
        busy={busy}
        onCancel={closeDialog}
        onConfirm={() => void runPairDecision()}
      >
        <Field label="Other project id">
          <Input
            value={otherId}
            onChange={(e) => setOtherId(e.target.value)}
            placeholder="project UUID"
          />
        </Field>
        {candidates.length ? (
          <Field label="Or pick a duplicate candidate">
            <Select value={otherId} onChange={(e) => setOtherId(e.target.value)}>
              <option value="">—</option>
              {candidates.map((candidate) => (
                <option key={candidate.project_id} value={candidate.project_id}>
                  {candidate.name || candidate.project_id}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field label="Verdict">
          <Select value={verdict} onChange={(e) => setVerdict(e.target.value)}>
            <option value="different">different — not the same initiative</option>
            <option value="related">related — distinct but connected</option>
            <option value="same">same — these are one initiative</option>
          </Select>
        </Field>
        <Field label="Reasoning (audited)">
          <Textarea
            value={reasoning}
            onChange={(e) => setReasoning(e.target.value)}
            placeholder="What in the evidence separates or joins these two?"
          />
        </Field>
        {verdict === "same" ? (
          <p className="text-body-sm text-tertiary leading-relaxed">
            Recording &quot;same&quot; does not merge anything. Use Merge to actually join the two
            records.
          </p>
        ) : null}
      </ConfirmDialog>
    </Panel>
  );
}

/* ========================================================================== *
 * PDR fields (unchanged)
 * ========================================================================== */

function PdrFieldRow({ fieldKey, data }: { fieldKey: string; data: ProjectData }) {
  const label = fieldLabel(fieldKey);
  const value = fieldValue(data, fieldKey);
  const reasoning = fieldReasoning(data, fieldKey);
  const quote = fieldQuote(data, fieldKey);
  const sourceUrl = fieldSourceUrl(data, fieldKey);
  const isLong = fieldKey === "project_description" || fieldKey === "project_roadmap";
  const hasAuditReasoning = Boolean(reasoning);

  if (!fieldHasContent(data, fieldKey)) {
    return null;
  }

  return (
    <section className="panel-border p-4 bg-surface-container-lowest">
      <FieldLabel className="mb-2">
        {label}
        {hasAuditReasoning ? (
          <span className="ml-2 text-tertiary normal-case tracking-normal">· reasoning</span>
        ) : null}
      </FieldLabel>
      {value ? (
        <p
          className={`text-body-sm text-on-surface ${
            isLong ? "leading-relaxed whitespace-pre-wrap" : "font-medium"
          }`}
        >
          {value}
        </p>
      ) : (
        <p className="text-body-sm text-on-surface-variant italic">No value set</p>
      )}
      {reasoning ? (
        <PdrReasoningBlock text={reasoning} quote={quote} sourceUrl={sourceUrl} />
      ) : null}
    </section>
  );
}

type Props = {
  project: ProjectDetail;
  className?: string;
  pdrProfile?: string;
  /** Called after any write that changed stored identity, so the list reloads. */
  onChanged?: () => void;
  /** Select another project in the owning page (merge survivor, split result). */
  onOpenProject?: (projectId: string) => void;
  /** Leads on this project changed (generated, sent to Search, decided). */
  onLeadsChanged?: () => void;
  /** Bumped by the page after bulk lead work, so the lead panels reload in place. */
  leadsRefreshKey?: number;
};

export function ProjectDetailView({
  project,
  className = "",
  pdrProfile,
  onChanged,
  onOpenProject,
  onLeadsChanged,
  leadsRefreshKey = 0,
}: Props) {
  const data = project.project_data || {};
  const lineage = project.lineage || [];
  const allKeys = tableFieldKeys(pdrProfile, [{ generated_pdr: data }]);
  const filledFields = allKeys.filter((k) => fieldHasContent(data, k));
  const emptyFields = allKeys.filter((k) => !fieldHasContent(data, k));
  const pdrProfileLabel = pdrProfileDisplayLabel(project.pdr_schema_profile || pdrProfile);
  const pdrComplete =
    normalizePdrProfile(project.pdr_schema_profile || pdrProfile) === PDR_PROFILE_COMPLETE;
  const discoverySources = project.discovery_sources || [];
  const projectId = project.project_id;

  /* ------------------------------------------------------ identity state -- */

  const [participants, setParticipants] = useState<ParticipantLike[]>([]);
  const [delivering, setDelivering] = useState<ParticipantLike[]>([]);
  const [mentions, setMentions] = useState<InitiativeMention[]>([]);
  const [identityLoading, setIdentityLoading] = useState(false);
  const [identityError, setIdentityError] = useState("");

  // Alias rows now come with the detail read (`project_aliases`), so the
  // retrieval scan is only the fallback for a record served without them.
  const [scannedAliases, setScannedAliases] = useState<string[]>([]);
  const [scannedAliasSource, setScannedAliasSource] = useState("");
  const [candidates, setCandidates] = useState<DuplicateCandidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesError, setCandidatesError] = useState("");
  const [targetCompanyId, setTargetCompanyId] = useState(project.target_company_id || "");

  const [conflicts, setConflicts] = useState<MergeConflict[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [relatedKey, setRelatedKey] = useState(0);


  /**
   * One GET per mention, capped at CANDIDATE_SCAN_LIMIT and preferring mentions
   * with no settled decision — those are the ones escalation queued. The same
   * response carries this project's own recorded aliases (retrieval returns the
   * mention's own project among the candidates) and its target_company_id;
   * both now arrive with the project record too, so these are the fallback.
   */
  const scanCandidates = useCallback(
    async (rows: InitiativeMention[]) => {
      if (!rows.length) {
        setCandidates([]);
        setCandidatesError("");
        return;
      }
      setCandidatesLoading(true);
      setCandidatesError("");
      const ranked = [...rows].sort((a, b) => {
        const aScore = UNSETTLED_MENTION_STATUSES.has(a.resolution_status) ? 0 : 1;
        const bScore = UNSETTLED_MENTION_STATUSES.has(b.resolution_status) ? 0 : 1;
        return aScore - bScore;
      });
      const scanned = ranked.slice(0, CANDIDATE_SCAN_LIMIT);
      const results = await Promise.allSettled(
        scanned.map((mention) => getMention(mention.mention_id, 8)),
      );
      const byProject = new Map<string, DuplicateCandidate>();
      const selfAliases = new Set<string>();
      let companyId = "";
      let failures = 0;
      let lastError = "";
      results.forEach((result, index) => {
        if (result.status === "rejected") {
          failures += 1;
          lastError = formatApiError(result.reason);
          return;
        }
        const payload = result.value;
        if (!companyId && payload.target_company_id) companyId = payload.target_company_id;
        const source = scanned[index];
        const unsettled = UNSETTLED_MENTION_STATUSES.has(source.resolution_status);
        for (const candidate of payload.candidates || []) {
          if (candidate.project_id === projectId) {
            for (const alias of candidate.aliases || []) selfAliases.add(alias);
            continue;
          }
          const existing = byProject.get(candidate.project_id);
          if (existing) {
            existing.cross_vendor = existing.cross_vendor || candidate.cross_vendor;
            existing.unsettled = existing.unsettled || unsettled;
            if (!existing.mention_ids.includes(source.mention_id)) {
              existing.mention_ids.push(source.mention_id);
            }
            continue;
          }
          byProject.set(candidate.project_id, {
            project_id: candidate.project_id,
            name: candidate.name,
            aliases: [...(candidate.aliases || [])],
            cross_vendor: Boolean(candidate.cross_vendor),
            mention_ids: [source.mention_id],
            unsettled,
          });
        }
      });
      const ordered = [...byProject.values()].sort((a, b) => {
        if (a.cross_vendor !== b.cross_vendor) return a.cross_vendor ? -1 : 1;
        if (a.unsettled !== b.unsettled) return a.unsettled ? -1 : 1;
        return b.mention_ids.length - a.mention_ids.length;
      });
      setCandidates(ordered);
      if (selfAliases.size) {
        setScannedAliases([...selfAliases].sort());
        setScannedAliasSource(
          `From project_aliases, read back through candidate retrieval over ${scanned.length} mention(s).`,
        );
      } else {
        setScannedAliases([]);
        setScannedAliasSource(
          rows.length
            ? "No alias rows came back with this project id."
            : "",
        );
      }
      if (companyId) setTargetCompanyId(companyId);
      setCandidatesError(
        failures
          ? `${failures} of ${scanned.length} mention lookups failed: ${lastError}`
          : "",
      );
      setCandidatesLoading(false);
    },
    [projectId],
  );

  const loadIdentity = useCallback(async () => {
    if (!projectId) return;
    setIdentityLoading(true);
    setIdentityError("");
    try {
      const data = await listProjectParticipants(projectId);
      const rows = data.mentions || [];
      setParticipants(data.participants || []);
      setDelivering(data.delivering_participants || []);
      setMentions(rows);
      await scanCandidates(rows);
    } catch (e) {
      // The render falls back to the participants the project record already
      // carried; both come from phase3_store, so the fallback is the same data
      // one read older rather than a different answer.
      setParticipants([]);
      setDelivering([]);
      setMentions([]);
      setCandidates([]);
      setIdentityError(
        `${formatApiError(e)} — falling back to the participants carried by the project record.`,
      );
    } finally {
      setIdentityLoading(false);
    }
  }, [projectId, scanCandidates]);

  useEffect(() => {
    setConflicts([]);
    setScannedAliases([]);
    setScannedAliasSource("");
    setTargetCompanyId(project.target_company_id || "");
    void loadIdentity();
  }, [loadIdentity, project.target_company_id]);

  /* The detail read is authoritative for aliases; the mention scan only fills
   * in for a payload that carries none (an older API build, or a list row
   * shown before the detail lands). */
  const recordAliases = useMemo(
    () =>
      [
        ...new Set(
          (project.aliases || []).map((alias) => String(alias || "").trim()).filter(Boolean),
        ),
      ].sort(),
    [project.aliases],
  );
  const aliases = recordAliases.length ? recordAliases : scannedAliases;
  const aliasSource = recordAliases.length
    ? "From project_aliases, served by GET /api/projects/{id}."
    : scannedAliasSource;

  const evidenceNames = useMemo(() => {
    const names = new Set<string>();
    for (const mention of mentions) {
      for (const key of ["program_names", "aliases"]) {
        const raw = mention.fields?.[key];
        if (Array.isArray(raw)) {
          for (const item of raw) {
            const text = String(item || "").trim();
            if (text) names.add(text);
          }
        } else if (typeof raw === "string" && raw.trim()) {
          names.add(raw.trim());
        }
      }
    }
    for (const alias of aliases) names.delete(alias);
    return [...names].sort().slice(0, 24);
  }, [mentions, aliases]);

  // The participants endpoint is authoritative; the project record's copy is the
  // fallback when that read fails or has not landed yet.
  const shownParticipants: ParticipantLike[] = participants.length
    ? participants
    : project.participants || [];
  const shownDelivering: ParticipantLike[] = delivering.length
    ? delivering
    : project.delivering_participants || [];

  const handleChanged = useCallback(() => {
    setRefreshKey((v) => v + 1);
    void loadIdentity();
    onChanged?.();
  }, [loadIdentity, onChanged]);

  const showMetaTags =
    Boolean(project.vendor_name) ||
    Boolean(project.pdr_reasoning_enabled) ||
    Boolean(project.pdr_schema_profile) ||
    discoverySources.length > 0 ||
    shownParticipants.length > 0;

  return (
    <div className={`flex flex-col min-h-0 ${className}`}>
      <header className="p-6 border-b border-outline-variant shrink-0">
        <FieldLabel className="font-data-mono normal-case">{project.target_company}</FieldLabel>
        <h2 className="font-headline-md text-headline-md text-on-surface mt-1">{project.project_name}</h2>
        <p className="text-body-sm font-data-mono text-on-surface-variant mt-1">
          {project.project_slug}
          {project.last_updated_at
            ? ` · updated ${new Date(project.last_updated_at).toLocaleString()}`
            : ""}
        </p>
        <p className="text-[10px] font-data-mono text-outline mt-1 select-all break-all">
          {project.project_id}
        </p>
        {showMetaTags ? (
          <div className="flex flex-wrap gap-2 mt-3">
            {project.status && project.status !== "active" ? (
              <Tag tone="error" title="This project id is not the active record">
                {project.status}
              </Tag>
            ) : null}
            {project.vendor_name ? <Tag tone="primary">{project.vendor_name}</Tag> : null}
            <Tag tone={pdrComplete ? "primary" : "neutral"}>{pdrProfileLabel}</Tag>
            {project.pdr_reasoning_enabled ? (
              <Tag tone="tertiary">Synthesis: reasoning enabled</Tag>
            ) : null}
            {discoverySources.map((src) => (
              <span
                key={src}
                className={`text-[9px] font-bold px-2 py-0.5 rounded border uppercase ${discoverySourceTagClass(src)}`}
                title={`Evidence from ${src}`}
              >
                {src}
              </span>
            ))}
            {shownParticipants.map((participant) => (
              <span
                key={participantKey(participant)}
                className="text-[9px] font-bold px-2 py-0.5 rounded border uppercase bg-secondary/10 text-secondary border-secondary/20"
                title={`${participant.role || "mentioned"}${participant.uncertainty ? " · provisional" : ""}`}
              >
                {participant.vendor_name}
              </span>
            ))}
          </div>
        ) : null}
        {project.target_company ? (
          <Link
            to={`/vendor-clusters?target=${encodeURIComponent(project.target_company)}`}
            className="inline-flex items-center gap-1 text-primary text-xs hover:underline mt-3"
          >
            <Icon name="hub" className="text-sm" />
            Open vendor clusters for {project.target_company}
          </Link>
        ) : null}
      </header>

      <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-3">
        <MergeConflictBanner conflicts={conflicts} onDismiss={() => setConflicts([])} />

        <ParticipantsPanel
          targetCompany={project.target_company}
          participants={shownParticipants}
          delivering={shownDelivering}
          loading={identityLoading}
          error={identityError}
          onRetry={() => void loadIdentity()}
        />

        <RoadmapDiscoveryPanel
          projectId={projectId}
          targetCompanyId={targetCompanyId || project.target_company_id}
          refreshKey={leadsRefreshKey}
          onOpenProject={onOpenProject}
          onLeadsChanged={() => {
            setRelatedKey((v) => v + 1);
            onLeadsChanged?.();
          }}
        />

        <RelatedProjectsPanel
          projectId={projectId}
          refreshKey={relatedKey + leadsRefreshKey}
          onOpenProject={onOpenProject}
        />

        {filledFields.length === 0 ? (
          <p className="text-sm text-on-surface-variant">
            No PDR fields populated yet for this project.
          </p>
        ) : (
          filledFields.map((key) => (
            <PdrFieldRow key={key} fieldKey={key} data={data} />
          ))
        )}

        {emptyFields.length > 0 ? (
          <details className="panel-border bg-surface-container-low/50 text-sm">
            <summary className="px-4 py-2 cursor-pointer text-on-surface-variant text-xs uppercase tracking-wide">
              Empty fields ({emptyFields.length})
            </summary>
            <ul className="px-4 pb-3 text-xs text-on-surface-variant font-data-mono space-y-1">
              {emptyFields.map((k) => (
                <li key={k}>{fieldLabel(k)}</li>
              ))}
            </ul>
          </details>
        ) : null}

        <IdentityPanel
          project={project}
          aliases={aliases}
          aliasSource={aliasSource}
          participants={shownParticipants}
          evidenceNames={evidenceNames}
          loading={candidatesLoading}
        />

        <OperationsPanel
          project={project}
          targetCompanyId={targetCompanyId}
          mentions={mentions}
          candidates={candidates}
          candidatesLoading={candidatesLoading}
          candidatesError={candidatesError}
          onRescan={() => void scanCandidates(mentions)}
          onConflicts={setConflicts}
          onChanged={handleChanged}
          onOpenProject={onOpenProject}
        />

        <RevisionsPanel
          projectId={projectId}
          targetCompanyId={targetCompanyId}
          clusterId={project.cluster_id || ""}
          refreshKey={refreshKey}
          onChanged={handleChanged}
        />

        <section className="panel-border p-4 bg-surface-container-low mt-4">
          <h3 className="text-[10px] uppercase tracking-widest text-on-surface-variant font-data-label mb-3 flex items-center gap-2">
            <Icon name="link" className="text-base" />
            Evidence lineage
          </h3>
          {!lineage.length ? (
            <p className="text-sm text-on-surface-variant">
              No linked evidence facts recorded for this project.
            </p>
          ) : (
            <ul className="space-y-3">
              {lineage.map((row, i) => (
                <li
                  key={`${row.evidence_id || row.url}-${i}`}
                  className="border-t border-outline-variant/50 pt-3 first:border-0 first:pt-0"
                >
                  <p className="text-sm text-on-surface leading-relaxed">{row.fact}</p>
                  {row.evidence_id ? (
                    <p className="text-[10px] font-data-mono text-on-surface-variant mt-1">
                      Evidence UUID:{" "}
                      <span className="text-primary select-all break-all">{row.evidence_id}</span>
                    </p>
                  ) : null}
                  {row.url ? (
                    <a
                      href={row.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary text-xs hover:underline mt-1 block truncate font-data-mono"
                    >
                      {row.title || row.url}
                    </a>
                  ) : null}
                  {row.discovery_mode ? (
                    <p className="text-[10px] text-on-surface-variant uppercase mt-1">
                      {row.discovery_mode}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
